import React, { type ReactNode } from "react";
import {
  normalizeFlowTraceIncidentMessage,
  sanitizeFlowTraceIncidentHref,
  sanitizeFlowTraceIncidentRoute,
  sanitizeFlowTraceIncidentText,
  stableFlowTraceIncidentHash,
} from "./incident-privacy";

export interface FlowTraceFrontendIncidentInput {
  build?: string;
  componentStack?: string;
  detail?: string;
  href?: string;
  level?: "error" | "warning";
  method?: string;
  observedAt?: string;
  route?: string;
  status?: number;
  summary: string;
}

export interface FlowTraceFrontendIncidentBody {
  build: string;
  componentStack: string;
  detail: string;
  fingerprint: string;
  href: string;
  level: "error" | "warning";
  method?: string;
  observedAt: string;
  route: string;
  status?: number;
  summary: string;
}

interface FlowTraceFrontendErrorBoundaryProps {
  children: ReactNode;
}

interface FlowTraceFrontendErrorBoundaryState {
  hasError: boolean;
}

const endpoint = "/api/frontend-incidents";
const service = "flowtrace-dashboard-frontend";
const recentSuppressMs = 5 * 60 * 1_000;
const recentFingerprints = new Map<string, number>();
let installed = false;

const viteEnv = (
  import.meta as ImportMeta & {
    env?: Record<string, string | undefined>;
  }
).env;
const runtimeBuild =
  String(viteEnv?.VITE_FLOWTRACE_BUILD_COMMIT ?? "").trim().slice(0, 80) ||
  "web";

const errorDetail = (value: unknown): string => {
  if (value instanceof Error) {
    return sanitizeFlowTraceIncidentText(value.stack || value.message, 4_000);
  }
  if (typeof value === "string") {
    return sanitizeFlowTraceIncidentText(value, 4_000);
  }
  return "Non-Error rejection";
};

const topStackFrame = (value: string): string => {
  const lines = sanitizeFlowTraceIncidentText(value, 4_000)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return normalizeFlowTraceIncidentMessage(
    lines.find((line) => line.startsWith("at ") || line.includes("@")) ?? "",
    240,
  ).toLowerCase();
};

export const buildFlowTraceFrontendIncidentBody = (
  input: FlowTraceFrontendIncidentInput,
  location?: { href: string; origin: string; pathname: string },
): FlowTraceFrontendIncidentBody => {
  const summary =
    normalizeFlowTraceIncidentMessage(input.summary || "Frontend incident") ||
    "Frontend incident";
  const detail = sanitizeFlowTraceIncidentText(input.detail, 4_000);
  const route = sanitizeFlowTraceIncidentRoute(
    input.route || location?.pathname || "/",
  );
  const href = sanitizeFlowTraceIncidentHref(
    input.href || location?.href || route,
    location?.origin,
  );
  const build =
    normalizeFlowTraceIncidentMessage(input.build || runtimeBuild, 80) || "web";
  const method = input.method
    ? normalizeFlowTraceIncidentMessage(input.method, 12).toUpperCase()
    : undefined;
  const status =
    Number.isInteger(input.status) &&
    Number(input.status) >= 100 &&
    Number(input.status) <= 599
      ? Number(input.status)
      : undefined;
  const observedAtCandidate = input.observedAt
    ? new Date(input.observedAt)
    : new Date();
  const observedAt = Number.isFinite(observedAtCandidate.getTime())
    ? observedAtCandidate.toISOString()
    : new Date().toISOString();
  const fingerprint = `${service}:${route}:${stableFlowTraceIncidentHash(
    [
      service,
      input.level || "error",
      method || "",
      status || "",
      route,
      summary.toLowerCase(),
      topStackFrame(detail || input.componentStack || ""),
      build,
    ].join("\n"),
  )}`;
  return {
    build,
    componentStack: sanitizeFlowTraceIncidentText(
      input.componentStack,
      2_000,
    ),
    detail,
    fingerprint,
    href,
    level: input.level || "error",
    method,
    observedAt,
    route,
    status,
    summary,
  };
};

const shouldSuppress = (fingerprint: string): boolean => {
  const now = Date.now();
  for (const [key, seenAt] of recentFingerprints) {
    if (seenAt + recentSuppressMs <= now) recentFingerprints.delete(key);
  }
  const seenAt = recentFingerprints.get(fingerprint);
  if (seenAt && seenAt + recentSuppressMs > now) return true;
  recentFingerprints.set(fingerprint, now);
  return false;
};

export const reportFlowTraceFrontendIncident = (
  input: FlowTraceFrontendIncidentInput,
): void => {
  if (typeof window === "undefined") return;
  try {
    const body = buildFlowTraceFrontendIncidentBody(input, window.location);
    if (shouldSuppress(body.fingerprint)) return;
    const serialized = JSON.stringify(body);
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      const accepted = navigator.sendBeacon(
        endpoint,
        new Blob([serialized], { type: "application/json" }),
      );
      if (accepted) return;
    }
    void fetch(endpoint, {
      body: serialized,
      credentials: "include",
      headers: { "Content-Type": "application/json; charset=utf-8" },
      keepalive: true,
      method: "POST",
    }).catch(() => {
      // Incident reporting must not create another visible application error.
    });
  } catch {
    // Incident reporting must never crash the dashboard.
  }
};

const readApiRequestMetadata = (
  input: RequestInfo | URL,
  init?: RequestInit,
): { method: string; reportable: boolean; route: string } => {
  try {
    const raw =
      typeof input === "string" || input instanceof URL
        ? input.toString()
        : input.url;
    const url = new URL(raw, window.location.href);
    const method = (
      init?.method ||
      (typeof input === "object" && "method" in input ? input.method : "GET")
    ).toUpperCase();
    return {
      method,
      reportable:
        url.origin === window.location.origin &&
        url.pathname.startsWith("/api/") &&
        url.pathname !== endpoint,
      route: sanitizeFlowTraceIncidentRoute(url.pathname),
    };
  } catch {
    return { method: "GET", reportable: false, route: "/" };
  }
};

const installFetchReporter = (): void => {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const metadata = readApiRequestMetadata(input, init);
    try {
      const response = await originalFetch(input, init);
      if (metadata.reportable && response.status >= 500) {
        reportFlowTraceFrontendIncident({
          method: metadata.method,
          route: window.location.pathname,
          status: response.status,
          summary: `${metadata.method} ${metadata.route} failed ${response.status}`,
        });
      }
      return response;
    } catch (error) {
      if (metadata.reportable) {
        reportFlowTraceFrontendIncident({
          detail: errorDetail(error),
          method: metadata.method,
          route: window.location.pathname,
          summary: `${metadata.method} ${metadata.route} network failure`,
        });
      }
      throw error;
    }
  };
};

export const installFlowTraceFrontendIncidentReporter = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  installFetchReporter();
  window.addEventListener("error", (event) => {
    reportFlowTraceFrontendIncident({
      detail: event.error
        ? errorDetail(event.error)
        : sanitizeFlowTraceIncidentText(
            `${event.filename || ""}:${event.lineno || 0}:${event.colno || 0}`,
            2_000,
          ),
      route: window.location.pathname,
      summary:
        normalizeFlowTraceIncidentMessage(
          event.message ||
            (event.error instanceof Error ? event.error.message : ""),
        ) || "Window error",
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportFlowTraceFrontendIncident({
      detail: errorDetail(event.reason),
      route: window.location.pathname,
      summary:
        normalizeFlowTraceIncidentMessage(
          event.reason instanceof Error
            ? event.reason.message
            : typeof event.reason === "string"
              ? event.reason
              : "",
        ) || "Unhandled rejection",
    });
  });
};

export class FlowTraceFrontendErrorBoundary extends React.Component<
  FlowTraceFrontendErrorBoundaryProps,
  FlowTraceFrontendErrorBoundaryState
> {
  state: FlowTraceFrontendErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): FlowTraceFrontendErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportFlowTraceFrontendIncident({
      componentStack: info.componentStack || "",
      detail: errorDetail(error),
      route: window.location.pathname,
      summary:
        normalizeFlowTraceIncidentMessage(error.message || error.name) ||
        "Render error",
    });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const isJapanese =
      typeof document !== "undefined" &&
      document.documentElement.lang.toLowerCase().startsWith("ja");
    return (
      <main className="full-page-status" role="alert">
        <div className="route-load-error">
          <p>
            {isJapanese
              ? "画面でエラーが発生しました。安全な範囲で incident 通知を試みました。"
              : "An error occurred. A privacy-bounded incident notification was attempted."}
          </p>
          <button onClick={() => window.location.reload()} type="button">
            {isJapanese ? "再読み込み" : "Reload"}
          </button>
        </div>
      </main>
    );
  }
}
